import { FeatureAttributes } from "../store/featureInfoStore";

/**
 * Pick the vector tile features worth showing in the feature info popup.
 *
 * MapLibre returns a clicked shape once per vector tile it spans, so the same feature
 * can appear several times in one click event. Dedupe on the tile feature id, falling
 * back to the attribute values when the tiles carry no ids, and drop attribute-less
 * features since there is nothing to show for them.
 *
 * Order is preserved, so the topmost feature stays first.
 */
export function inspectableAttributes(features: any[]): FeatureAttributes[] {
  const seen = new Set<string>();
  const unique: FeatureAttributes[] = [];

  for (const feature of features) {
    const properties: FeatureAttributes = feature?.properties ?? {};
    if (Object.keys(properties).length === 0) continue;

    const key = feature?.id != null ? String(feature.id) : JSON.stringify(properties);
    if (seen.has(key)) continue;

    seen.add(key);
    unique.push(properties);
  }

  return unique;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;
const DECIMAL_STRING = /^-?\d+\.\d+$/;
/** Tile attributes often pack repeating values into one delimited string. */
const MULTI_VALUE_SEPARATOR = ";";

/**
 * Dates are shown in the timezone they were recorded in: a UTC timestamp rendered in
 * local time can land on the wrong day, which matters for heritage and survey records.
 */
function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const timeZone = value.endsWith("Z") ? "UTC" : undefined;
  const day = date.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric", timeZone });
  const hasMeaningfulTime = value.includes("T") && !/T00:00(:00)?(\.0+)?(Z|[+-])?/.test(value);

  return hasMeaningfulTime ? `${day}, ${date.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", timeZone })}` : day;
}

/** Long floats out of a GIS pipeline (areas, perimeters) are noise past a few decimals. */
function formatNumber(value: number): string {
  if (Number.isInteger(value)) return value.toLocaleString("en-AU");
  return value.toLocaleString("en-AU", { maximumFractionDigits: Math.abs(value) >= 1 ? 2 : 4 });
}

/** Render one tile attribute as readable text. */
export function formatAttributeValue(value: unknown): string {
  if (value === null || value === undefined || value === "" || value === "null") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return formatNumber(value);
  if (typeof value === "object") return JSON.stringify(value);

  const text = String(value).trim();
  if (ISO_DATE.test(text)) return formatDate(text);
  if (DECIMAL_STRING.test(text)) return formatNumber(Number(text));

  return text;
}

/**
 * Split a delimited attribute into its parts, which read far better stacked than
 * wrapped as one long sentence. Returns null when there is nothing to split.
 */
export function splitMultiValue(value: unknown): string[] | null {
  if (typeof value !== "string" || !value.includes(MULTI_VALUE_SEPARATOR)) return null;

  const parts = value
    .split(MULTI_VALUE_SEPARATOR)
    .map((part) => part.trim())
    .filter(Boolean);

  return parts.length > 1 ? parts : null;
}

const TITLE_KEY = /(^|[_\s-])(name|title|label)$/i;
const INTERNAL_KEY = /^(fid|ogc_fid|objectid|object_id|oid|gid|id|shape_leng(th)?|shape_area|st_area(_sh)?|st_length|geom|geometry|wkt)$/i;

function hasAttributeValue(value: unknown): boolean {
  return value !== null && value !== undefined && value !== "" && value !== "null";
}

/**
 * Prefer a name-like attribute as the popup title so the card is about the
 * feature (Quanbun Downs), not the upload (Pastoral Stations).
 */
export function pickFeatureTitle(attrs: FeatureAttributes, fallback: string): { key: string | null; title: string } {
  for (const [key, value] of Object.entries(attrs)) {
    if (!TITLE_KEY.test(key) || !hasAttributeValue(value)) continue;
    return { key, title: String(value).trim() };
  }

  return { key: null, title: fallback };
}

/**
 * Every remaining attribute, title omitted so it is not repeated under the heading.
 * GIS internals stay at the bottom — we do not try to guess which fields matter.
 */
export function listFeatureAttributes(attrs: FeatureAttributes, titleKey: string | null): [string, unknown][] {
  const remaining = Object.entries(attrs).filter(([key]) => key !== titleKey);
  const interesting: [string, unknown][] = [];
  const internals: [string, unknown][] = [];

  for (const entry of remaining) {
    if (INTERNAL_KEY.test(entry[0])) internals.push(entry);
    else interesting.push(entry);
  }

  return [...interesting, ...internals];
}
